// Marketing-flyer i18n: fixed "chrome" copy + canonical benefit short-labels,
// translated into English + 6 major regional languages. Admin picks which
// canonical benefit keys apply per state (₹ amount + benefit_keys); the SC
// picks a language and every string renders fully translated (no freeform
// text to translate, so quality/consistency is guaranteed across languages).
//
// "Purvodaya Solar" / "Purvodaya" is always kept in Latin script across every
// language (brand name is never transliterated/translated).
export const FLYER_LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "bn", label: "বাংলা" },
];

export const BENEFIT_KEYS = ["zero_bill", "clean_energy", "subsidy", "property_value", "low_maintenance", "quick_payback"];

// Central PM Surya Ghar Yojana CFA cap for a typical 3kW residential system —
// kept in sync with backend's models.CENTRAL_SUBSIDY_AMOUNT. States may add
// their own top-up on top of this (see StateBenefits.jsx admin page).
export const CENTRAL_SUBSIDY_AMOUNT = 78000;

export const BENEFIT_KEY_LABELS_EN = {
  zero_bill: "Save on Electricity Bills",
  clean_energy: "Clean, Green & Renewable Energy",
  subsidy: "Government Subsidy",
  property_value: "Increase Property Value",
  low_maintenance: "Low Maintenance & Long Life",
  quick_payback: "Payback in 4-5 Years",
};

const inr = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

export const FLYER_I18N = {
  en: {
    brandTagline: "Clean Energy. Brighter Tomorrow.",
    headlineLine1: "Go Solar,",
    headlineLine2: "Save Electricity",
    subheadline: "A brighter home. A smarter energy choice.",
    paragraph: "Power your home with rooftop solar and take control of your electricity costs.",
    growthTagline: "Participate in the Growth of India",
    subsidyPrefix: "Avail benefits up to",
    subsidyScheme: "under PM Surya Ghar Yojana",
    checklist: ["Premium solar panels", "Expert installation", "Net metering support", "After-sales service"],
    consultLabel: "Get a Free Consultation",
    ctaQr: "Scan for a callback",
    footerTagline: "Powering a Self-Reliant & Sustainable India",
    bottomLabels: { ...BENEFIT_KEY_LABELS_EN, subsidy: `Government Subsidy\nUp to ${inr(78000)}` },
    subsidyAmountLabel: (amt) => `Government Subsidy\nUp to ${inr(amt)}`,
    advantageWord: "Advantage",
    reliableSupportTagline: "Reliable solar support, from start to finish.",
    letsTalkHeading: "Let's Talk Solar",
    qrScanDesc: "Scan the QR code and a Purvodaya Solar specialist will reach out to you shortly.",
    additionalEnquiries: "Additional Enquiries",
    upToBefore: "UP TO ", upToAfter: "",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} extra from ${state} Government`,
    financingBadge: "Easy Financing Available from Top Banks",
  },
  hi: {
    brandTagline: "स्वच्छ ऊर्जा। उज्जवल भविष्य।",
    headlineLine1: "सोलर अपनाएं,",
    headlineLine2: "बिजली बचाएं",
    subheadline: "एक रोशन घर। एक समझदार ऊर्जा विकल्प।",
    paragraph: "अपने घर को रूफटॉप सोलर से पावर दें और अपने बिजली खर्च पर नियंत्रण पाएं।",
    growthTagline: "भारत की प्रगति में भागीदार बनें",
    subsidyPrefix: "पाएं लाभ",
    subsidyScheme: "PM सूर्य घर योजना के अंतर्गत",
    checklist: ["उच्च गुणवत्ता सोलर पैनल", "एक्सपर्ट इंस्टॉलेशन", "नेट मीटरिंग सपोर्ट", "आफ्टर सेल्स सर्विस"],
    consultLabel: "फ्री सलाह लें",
    ctaQr: "कॉलबैक के लिए स्कैन करें",
    footerTagline: "आत्मनिर्भर व सतत भारत की ओर",
    subsidyAmountLabel: (amt) => `सरकारी सब्सिडी\n${inr(amt)} तक`,
    bottomLabelsMap: {
      zero_bill: "बिजली बिल में बचत",
      clean_energy: "स्वच्छ, हरित ऊर्जा",
      property_value: "प्रॉपर्टी वैल्यू बढ़ाएं",
      low_maintenance: "कम रखरखाव, लंबी उम्र",
      quick_payback: "4-5 वर्षों में लागत वसूल",
    },
    advantageWord: "फायदे",
    reliableSupportTagline: "शुरू से अंत तक, भरोसेमंद सोलर सहायता।",
    letsTalkHeading: "सोलर की बात करें",
    qrScanDesc: "QR कोड स्कैन करें, Purvodaya Solar का विशेषज्ञ जल्द ही आपसे संपर्क करेगा।",
    additionalEnquiries: "अन्य जानकारी हेतु",
    upToBefore: "", upToAfter: " तक",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} अतिरिक्त, ${state} सरकार से`,
    financingBadge: "टॉप बैंकों से आसान फाइनेंसिंग उपलब्ध",
  },
  mr: {
    brandTagline: "स्वच्छ ऊर्जा. उज्ज्वल उद्या.",
    headlineLine1: "सोलरकडे वळा,",
    headlineLine2: "वीज वाचवा",
    subheadline: "एक उजळ घर. एक स्मार्ट ऊर्जा निवड.",
    paragraph: "तुमचे घर रूफटॉप सोलरने पॉवर करा आणि तुमच्या वीज खर्चावर नियंत्रण मिळवा.",
    growthTagline: "भारताच्या प्रगतीत सहभागी व्हा",
    subsidyPrefix: "मिळवा लाभ",
    subsidyScheme: "PM सूर्य घर योजनेअंतर्गत",
    checklist: ["उच्च दर्जाचे सोलर पॅनेल", "तज्ज्ञांकडून इंस्टॉलेशन", "नेट मीटरिंग सपोर्ट", "आफ्टर सेल्स सर्व्हिस"],
    consultLabel: "मोफत सल्ला घ्या",
    ctaQr: "कॉलबॅकसाठी स्कॅन करा",
    footerTagline: "स्वयंपूर्ण व शाश्वत भारताकडे",
    subsidyAmountLabel: (amt) => `सरकारी अनुदान\n${inr(amt)} पर्यंत`,
    bottomLabelsMap: {
      zero_bill: "वीज बिलात बचत",
      clean_energy: "स्वच्छ, हरित ऊर्जा",
      property_value: "प्रॉपर्टीचे मूल्य वाढवा",
      low_maintenance: "कमी देखभाल, दीर्घ आयुष्य",
      quick_payback: "4-5 वर्षांत खर्च वसूल",
    },
    advantageWord: "फायदे",
    reliableSupportTagline: "सुरुवातीपासून शेवटपर्यंत, विश्वासार्ह सोलर सहाय्य.",
    letsTalkHeading: "सोलरबद्दल बोलूया",
    qrScanDesc: "QR कोड स्कॅन करा, Purvodaya Solar चा तज्ज्ञ लवकरच तुमच्याशी संपर्क साधेल.",
    additionalEnquiries: "अधिक माहितीसाठी",
    upToBefore: "", upToAfter: " पर्यंत",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} अतिरिक्त, ${state} सरकारकडून`,
    financingBadge: "टॉप बँकांकडून सोपी फायनान्सिंग उपलब्ध",
  },
  gu: {
    brandTagline: "સ્વચ્છ ઊર્જા. ઉજ્જવળ આવતીકાલ.",
    headlineLine1: "સોલર અપનાવો,",
    headlineLine2: "વીજળી બચાવો",
    subheadline: "એક ઉજ્જવળ ઘર. એક સ્માર્ટ ઊર્જા પસંદગી.",
    paragraph: "તમારા ઘરને રૂફટોપ સોલરથી પાવર આપો અને તમારા વીજળી ખર્ચ પર નિયંત્રણ મેળવો.",
    growthTagline: "ભારતની પ્રગતિમાં ભાગીદાર બનો",
    subsidyPrefix: "મેળવો લાભ",
    subsidyScheme: "PM સૂર્ય ઘર યોજના અંતર્ગત",
    checklist: ["ઉચ્ચ ગુણવત્તાવાળા સોલર પેનલ", "એક્સપર્ટ ઇન્સ્ટોલેશન", "નેટ મીટરિંગ સપોર્ટ", "આફ્ટર સેલ્સ સર્વિસ"],
    consultLabel: "ફ્રી સલાહ મેળવો",
    ctaQr: "કોલબેક માટે સ્કેન કરો",
    footerTagline: "આત્મનિર્ભર અને ટકાઉ ભારત તરફ",
    subsidyAmountLabel: (amt) => `સરકારી સબસિડી\n${inr(amt)} સુધી`,
    bottomLabelsMap: {
      zero_bill: "વીજ બિલમાં બચત",
      clean_energy: "સ્વચ્છ, હરિત ઊર્જા",
      property_value: "પ્રોપર્ટીની કિંમત વધારો",
      low_maintenance: "ઓછી જાળવણી, લાંબુ આયુષ્ય",
      quick_payback: "4-5 વર્ષમાં ખર્ચ વસૂલ",
    },
    advantageWord: "ફાયદા",
    reliableSupportTagline: "શરૂઆતથી અંત સુધી, વિશ્વસનીય સોલર સહાય.",
    letsTalkHeading: "સોલર વિશે વાત કરીએ",
    qrScanDesc: "QR કોડ સ્કેન કરો, Purvodaya Solar નો નિષ્ણાત ટૂંક સમયમાં તમારો સંપર્ક કરશે.",
    additionalEnquiries: "વધુ માહિતી માટે",
    upToBefore: "", upToAfter: " સુધી",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} વધારાના, ${state} સરકાર તરફથી`,
    financingBadge: "ટોચની બેંકો તરફથી સરળ ફાઇનાન્સિંગ ઉપલબ્ધ",
  },
  ta: {
    brandTagline: "சுத்தமான ஆற்றல். சிறந்த நாளை.",
    headlineLine1: "சூரிய சக்திக்கு மாறுங்கள்,",
    headlineLine2: "மின்சாரத்தை சேமியுங்கள்",
    subheadline: "ஒரு பிரகாசமான வீடு. ஒரு புத்திசாலித்தனமான ஆற்றல் தேர்வு.",
    paragraph: "உங்கள் வீட்டிற்கு ரூஃப்டாப் சோலார் மூலம் பவர் கொடுத்து, உங்கள் மின்சார செலவை கட்டுப்படுத்துங்கள்.",
    growthTagline: "இந்தியாவின் வளர்ச்சியில் பங்கேற்பாளராகுங்கள்",
    subsidyPrefix: "பெறுங்கள் பலன்கள்",
    subsidyScheme: "PM சூர்ய கர் யோஜனாவின் கீழ்",
    checklist: ["உயர்தர சோலார் பேனல்கள்", "நிபுணர் நிறுவல்", "நெட் மீட்டரிங் ஆதரவு", "விற்பனைக்கு பிந்தைய சேவை"],
    consultLabel: "இலவச ஆலோசனை பெறுங்கள்",
    ctaQr: "கால்பேக்கிற்கு ஸ்கேன் செய்யுங்கள்",
    footerTagline: "தன்னிறைவான, நிலைத்த இந்தியாவை நோக்கி",
    subsidyAmountLabel: (amt) => `அரசு மானியம்\n${inr(amt)} வரை`,
    bottomLabelsMap: {
      zero_bill: "மின் கட்டணத்தில் சேமிப்பு",
      clean_energy: "சுத்தமான, பசுமை ஆற்றல்",
      property_value: "சொத்தின் மதிப்பை உயர்த்தும்",
      low_maintenance: "குறைந்த பராமரிப்பு, நீண்ட ஆயுள்",
      quick_payback: "4-5 ஆண்டுகளில் முதலீடு திரும்பும்",
    },
    advantageWord: "நன்மைகள்",
    reliableSupportTagline: "ஆரம்பம் முதல் முடிவு வரை, நம்பகமான சோலார் ஆதரவு.",
    letsTalkHeading: "சோலார் பற்றி பேசலாம்",
    qrScanDesc: "QR குறியீட்டை ஸ்கேன் செய்யுங்கள், Purvodaya Solar நிபுணர் விரைவில் உங்களை தொடர்பு கொள்வார்.",
    additionalEnquiries: "மேலும் விவரங்களுக்கு",
    upToBefore: "", upToAfter: " வரை",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} கூடுதல், ${state} அரசிடமிருந்து`,
    financingBadge: "முன்னணி வங்கிகளிடமிருந்து எளிதான நிதியுதவி கிடைக்கிறது",
  },
  te: {
    brandTagline: "స్వచ్ఛ శక్తి. ఉజ్వల భవిష్యత్తు.",
    headlineLine1: "సోలార్‌కు మారండి,",
    headlineLine2: "విద్యుత్‌ను ఆదా చేయండి",
    subheadline: "ఒక ప్రకాశవంతమైన ఇల్లు. ఒక తెలివైన శక్తి ఎంపిక.",
    paragraph: "మీ ఇంటికి రూఫ్‌టాప్ సోలార్‌తో పవర్ ఇవ్వండి మరియు మీ విద్యుత్ ఖర్చుపై నియంత్రణ పొందండి.",
    growthTagline: "భారత అభివృద్ధిలో భాగస్వామి కండి",
    subsidyPrefix: "పొందండి ప్రయోజనాలు",
    subsidyScheme: "PM సూర్య ఘర్ యోజన కింద",
    checklist: ["అత్యుత్తమ సోలార్ పానెల్స్", "నిపుణుల ఇన్‌స్టాలేషన్", "నెట్ మీటరింగ్ సపోర్ట్", "ఆఫ్టర్ సేల్స్ సర్వీస్"],
    consultLabel: "ఫ్రీ కన్సల్టేషన్ పొందండి",
    ctaQr: "కాల్‌బ్యాక్ కోసం స్కాన్ చేయండి",
    footerTagline: "స్వావలంబన, సుస్థిర భారత్ కోసం",
    subsidyAmountLabel: (amt) => `ప్రభుత్వ సబ్సిడీ\n${inr(amt)} వరకు`,
    bottomLabelsMap: {
      zero_bill: "విద్యుత్ బిల్లులో ఆదా",
      clean_energy: "శుభ్రమైన, పునరుత్పాదక శక్తి",
      property_value: "ప్రాపర్టీ విలువను పెంచుతుంది",
      low_maintenance: "తక్కువ నిర్వహణ, ఎక్కువ మనుగడ",
      quick_payback: "4-5 సంవత్సరాల్లో ఖర్చు తిరిగి వస్తుంది",
    },
    advantageWord: "ప్రయోజనాలు",
    reliableSupportTagline: "ప్రారంభం నుండి ముగింపు వరకు, నమ్మకమైన సోలార్ మద్దతు.",
    letsTalkHeading: "సోలార్ గురించి మాట్లాడదాం",
    qrScanDesc: "QR కోడ్‌ను స్కాన్ చేయండి, Purvodaya Solar నిపుణుడు త్వరలో మిమ్మల్ని సంప్రదిస్తారు.",
    additionalEnquiries: "మరింత సమాచారం కోసం",
    upToBefore: "", upToAfter: " వరకు",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} అదనంగా, ${state} ప్రభుత్వం నుండి`,
    financingBadge: "టాప్ బ్యాంకుల నుండి సులభమైన ఫైనాన్సింగ్ అందుబాటులో ఉంది",
  },
  kn: {
    brandTagline: "ಶುದ್ಧ ಶಕ್ತಿ. ಉಜ್ವಲ ನಾಳೆ.",
    headlineLine1: "ಸೌರಶಕ್ತಿಗೆ ಬದಲಾಗಿ,",
    headlineLine2: "ವಿದ್ಯುತ್ ಉಳಿಸಿ",
    subheadline: "ಒಂದು ಪ್ರಕಾಶಮಾನ ಮನೆ. ಒಂದು ಸ್ಮಾರ್ಟ್ ಶಕ್ತಿ ಆಯ್ಕೆ.",
    paragraph: "ನಿಮ್ಮ ಮನೆಗೆ ರೂಫ್‌ಟಾಪ್ ಸೌರಶಕ್ತಿಯಿಂದ ಪವರ್ ನೀಡಿ ಮತ್ತು ನಿಮ್ಮ ವಿದ್ಯುತ್ ವೆಚ್ಚದ ಮೇಲೆ ನಿಯಂತ್ರಣ ಪಡೆಯಿರಿ.",
    growthTagline: "ಭಾರತದ ಪ್ರಗತಿಯಲ್ಲಿ ಪಾಲುದಾರರಾಗಿ",
    subsidyPrefix: "ಪಡೆಯಿರಿ ಪ್ರಯೋಜನಗಳು",
    subsidyScheme: "PM ಸೂರ್ಯ ಘರ್ ಯೋಜನೆಯಡಿ",
    checklist: ["ಉತ್ತಮ ಗುಣಮಟ್ಟದ ಸೌರ ಪ್ಯಾನೆಲ್‌ಗಳು", "ತಜ್ಞರ ಅನುಸ್ಥಾಪನೆ", "ನೆಟ್ ಮೀಟರಿಂಗ್ ಸಪೋರ್ಟ್", "ಆಫ್ಟರ್ ಸೇಲ್ಸ್ ಸೇವೆ"],
    consultLabel: "ಉಚಿತ ಸಲಹೆ ಪಡೆಯಿರಿ",
    ctaQr: "ಕಾಲ್‌ಬ್ಯಾಕ್‌ಗಾಗಿ ಸ್ಕ್ಯಾನ್ ಮಾಡಿ",
    footerTagline: "ಸ್ವಾವಲಂಬಿ, ಸುಸ್ಥಿರ ಭಾರತದತ್ತ",
    subsidyAmountLabel: (amt) => `ಸರ್ಕಾರಿ ಸಬ್ಸಿಡಿ\n${inr(amt)} ವರೆಗೆ`,
    bottomLabelsMap: {
      zero_bill: "ವಿದ್ಯುತ್ ಬಿಲ್‌ನಲ್ಲಿ ಉಳಿತಾಯ",
      clean_energy: "ಶುದ್ಧ, ಹಸಿರು ಶಕ್ತಿ",
      property_value: "ಆಸ್ತಿಯ ಮೌಲ್ಯ ಹೆಚ್ಚಿಸುತ್ತದೆ",
      low_maintenance: "ಕಡಿಮೆ ನಿರ್ವಹಣೆ, ದೀರ್ಘ ಬಾಳಿಕೆ",
      quick_payback: "4-5 ವರ್ಷಗಳಲ್ಲಿ ಬಂಡವಾಳ ಮರಳಿ",
    },
    advantageWord: "ಪ್ರಯೋಜನಗಳು",
    reliableSupportTagline: "ಆರಂಭದಿಂದ ಅಂತ್ಯದವರೆಗೆ, ವಿಶ್ವಾಸಾರ್ಹ ಸೌರ ಬೆಂಬಲ.",
    letsTalkHeading: "ಸೌರಶಕ್ತಿ ಬಗ್ಗೆ ಮಾತನಾಡೋಣ",
    qrScanDesc: "QR ಕೋಡ್ ಸ್ಕ್ಯಾನ್ ಮಾಡಿ, Purvodaya Solar ತಜ್ಞರು ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತಾರೆ.",
    additionalEnquiries: "ಹೆಚ್ಚಿನ ಮಾಹಿತಿಗಾಗಿ",
    upToBefore: "", upToAfter: " ವರೆಗೆ",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} ಹೆಚ್ಚುವರಿ, ${state} ಸರ್ಕಾರದಿಂದ`,
    financingBadge: "ಪ್ರಮುಖ ಬ್ಯಾಂಕ್‌ಗಳಿಂದ ಸುಲಭ ಫೈನಾನ್ಸಿಂಗ್ ಲಭ್ಯವಿದೆ",
  },
  bn: {
    brandTagline: "পরিষ্কার শক্তি। উজ্জ্বল আগামী।",
    headlineLine1: "সোলারে যান,",
    headlineLine2: "বিদ্যুৎ সাশ্রয় করুন",
    subheadline: "একটি উজ্জ্বল বাড়ি। একটি স্মার্ট শক্তির পছন্দ।",
    paragraph: "আপনার বাড়িতে রুফটপ সোলার দিয়ে পাওয়ার দিন এবং আপনার বিদ্যুৎ খরচের উপর নিয়ন্ত্রণ পান।",
    growthTagline: "ভারতের উন্নয়নে অংশীদার হন",
    subsidyPrefix: "পান সুবিধা",
    subsidyScheme: "PM সূর্য ঘর যোজনার অধীনে",
    checklist: ["উচ্চমানের সোলার প্যানেল", "বিশেষজ্ঞ ইনস্টলেশন", "নেট মিটারিং সাপোর্ট", "আফটার সেলস সার্ভিস"],
    consultLabel: "ফ্রি পরামর্শ নিন",
    ctaQr: "কলব্যাকের জন্য স্ক্যান করুন",
    footerTagline: "স্বনির্ভর ও টিকে থাকার ভারতের পথে",
    subsidyAmountLabel: (amt) => `সরকারি ভর্তুকি\n${inr(amt)} পর্যন্ত`,
    bottomLabelsMap: {
      zero_bill: "বিদ্যুৎ বিলে সাশ্রয়",
      clean_energy: "পরিষ্কার, সবুজ শক্তি",
      property_value: "সম্পত্তির মূল্য বাড়ায়",
      low_maintenance: "কম রক্ষণাবেক্ষণ, দীর্ঘ আয়ু",
      quick_payback: "4-5 বছরে খরচ ফেরত",
    },
    advantageWord: "সুবিধা",
    reliableSupportTagline: "শুরু থেকে শেষ পর্যন্ত, নির্ভরযোগ্য সোলার সহায়তা।",
    letsTalkHeading: "সোলার নিয়ে কথা বলি",
    qrScanDesc: "QR কোড স্ক্যান করুন, Purvodaya Solar-এর একজন বিশেষজ্ঞ শীঘ্রই আপনার সাথে যোগাযোগ করবেন।",
    additionalEnquiries: "আরও তথ্যের জন্য",
    upToBefore: "", upToAfter: " পর্যন্ত",
    stateTopupLabel: (state, amt) => `+ ${inr(amt)} অতিরিক্ত, ${state} সরকার থেকে`,
    financingBadge: "শীর্ষ ব্যাংক থেকে সহজ ফাইন্যান্সিং উপলব্ধ",
  },
};

// Resolve a benefit key's short bottom-icon label in the given language,
// falling back to English where a regional map entry isn't defined.
export function bottomLabel(lang, key, subsidyAmount) {
  const t = FLYER_I18N[lang] || FLYER_I18N.en;
  if (key === "subsidy") return (t.subsidyAmountLabel || FLYER_I18N.en.subsidyAmountLabel)(subsidyAmount);
  return (t.bottomLabelsMap && t.bottomLabelsMap[key]) || BENEFIT_KEY_LABELS_EN[key] || key;
}

// Per-campaign-category (poster) translations. "all" (Residential) only carries
// the pieces not already covered by FLYER_I18N's top-level fields (tag, footer
// heading, disclaimer, badge, subsidy footer) — headline/subheadline/paragraph/
// checklist for Residential already come from the top-level translated fields.
// "housing_society" and "street_light" carry every field since they have no
// top-level fallback. Benefit `iconKey` maps to an Icon component in ScFlyer.jsx.
export const FLYER_CATEGORY_I18N = {
  en: {
    all: {
      tag: "RESIDENTIAL ROOFTOP SOLAR",
      footerHeading: "New installation or service support?",
      subsidyFooter: "GOVERNMENT SUBSIDY*",
      disclaimer: "*Subsidy amount depends on system size & state; subject to MNRE / PM Surya Ghar Yojana guidelines.",
      badge: ["PM SURYA", "GHAR YOJANA"],
    },
    housing_society: {
      headlineLine1: "POWER YOUR", headlineLine2: "ENTIRE SOCIETY.",
      subheadline: "Shared solar. Shared savings for every resident.",
      paragraph: "Install rooftop solar for your society's common lighting, lifts, pumps & EV charging points — and lower the maintenance bill for everyone.",
      checklist: ["Covers common lighting, lifts & EV points", "Up to 500 kW capacity per society", "Dedicated RWA / AOA support team", "Net metering on the society's own meter"],
      tag: "HOUSING SOCIETY SOLAR",
      footerHeading: "New installation for your society?",
      subsidy: { eyebrow: "PM SURYA GHAR YOJANA · GHS / RWA", big: "₹18,000", unit: "PER kW", footer: "CENTRAL SUBSIDY FOR COMMON AREAS*", showUpTo: true },
      disclaimer: "*Up to 500 kW per society (₹19,800/kW in hill & NE states); for common areas only, as per MNRE guidelines.",
      badge: ["GHS / RWA", "SCHEME"],
      benefits: [
        { iconKey: "zap", label: "Lower common\narea bills" },
        { iconKey: "leaf", label: "Clean, green\nenergy" },
        { iconKey: "landmark", label: "₹18,000/kW\ngovt. subsidy" },
        { iconKey: "trendingUp", label: "Higher resale\nvalue" },
        { iconKey: "shieldCheck", label: "25-year\npanel life" },
      ],
    },
    street_light: {
      headlineLine1: "LIGHT UP YOUR", headlineLine2: "STREETS. FOR FREE.",
      subheadline: "Zero electricity bill. Zero grid dependency.",
      paragraph: "Solar-powered street lighting for municipalities, panchayats & gated roads — bright, safe streets that cost nothing to run.",
      checklist: ["No grid electricity bill, ever", "Automatic dusk-to-dawn operation", "Low maintenance, 25-year life", "Eligible for govt. tenders & CSR funding"],
      tag: "STREET LIGHT SOLAR",
      footerHeading: "Street light installation or service support?",
      subsidy: { eyebrow: "SOLAR STREET LIGHTING", big: "ZERO", unit: "", footer: "ELECTRICITY BILL · GOVT. TENDER & CSR SUPPORT*" },
      disclaimer: "*Funded via state / panchayat / municipal budgets, CSR & GeM tenders — availability varies by state and project.",
      badge: ["SOLAR", "POWERED"],
      benefits: [
        { iconKey: "zap", label: "Zero running\ncost" },
        { iconKey: "leaf", label: "Clean, green\nenergy" },
        { iconKey: "landmark", label: "Govt. tender &\nCSR support" },
        { iconKey: "clock", label: "Dusk-to-dawn\nauto on/off" },
        { iconKey: "shieldCheck", label: "Low\nmaintenance" },
      ],
    },
  },
  hi: {
    all: {
      tag: "रेजिडेंशियल रूफटॉप सोलर",
      footerHeading: "नई इंस्टॉलेशन या सर्विस सपोर्ट चाहिए?",
      subsidyFooter: "सरकारी सब्सिडी*",
      disclaimer: "*सब्सिडी राशि सिस्टम साइज़ और राज्य पर निर्भर है; MNRE / PM सूर्य घर योजना के नियमों के अनुसार।",
      badge: ["PM सूर्य", "घर योजना"],
    },
    housing_society: {
      headlineLine1: "अपनी पूरी", headlineLine2: "सोसाइटी को पावर दें।",
      subheadline: "साझा सोलर। हर निवासी के लिए साझा बचत।",
      paragraph: "अपनी सोसाइटी की कॉमन लाइटिंग, लिफ्ट, पंप और EV चार्जिंग पॉइंट्स के लिए रूफटॉप सोलर लगवाएं — और सबके लिए मेंटेनेंस बिल घटाएं।",
      checklist: ["कॉमन लाइटिंग, लिफ्ट व EV पॉइंट्स कवर", "सोसाइटी के लिए 500 kW तक क्षमता", "समर्पित RWA / AOA सपोर्ट टीम", "सोसाइटी के अपने मीटर पर नेट मीटरिंग"],
      tag: "हाउसिंग सोसाइटी सोलर",
      footerHeading: "अपनी सोसाइटी के लिए नई इंस्टॉलेशन चाहिए?",
      subsidy: { eyebrow: "PM सूर्य घर योजना · GHS / RWA", big: "₹18,000", unit: "प्रति kW", footer: "कॉमन एरिया के लिए केंद्रीय सब्सिडी*", showUpTo: true },
      disclaimer: "*प्रति सोसाइटी 500 kW तक (पहाड़ी व NE राज्यों में ₹19,800/kW); केवल कॉमन एरिया के लिए, MNRE नियमों के अनुसार।",
      badge: ["GHS / RWA", "योजना"],
      benefits: [
        { iconKey: "zap", label: "कॉमन एरिया\nबिल में कमी" },
        { iconKey: "leaf", label: "स्वच्छ, हरित\nऊर्जा" },
        { iconKey: "landmark", label: "₹18,000/kW\nसरकारी सब्सिडी" },
        { iconKey: "trendingUp", label: "प्रॉपर्टी की\nऊंची वैल्यू" },
        { iconKey: "shieldCheck", label: "25 वर्ष\nपैनल लाइफ" },
      ],
    },
    street_light: {
      headlineLine1: "अपनी सड़कों को", headlineLine2: "मुफ्त में रोशन करें।",
      subheadline: "ज़ीरो बिजली बिल। ज़ीरो ग्रिड निर्भरता।",
      paragraph: "नगर पालिकाओं, पंचायतों व गेटेड रोड्स के लिए सोलर स्ट्रीट लाइटिंग — रोशन, सुरक्षित सड़कें जिनकी चलाने में कोई लागत नहीं।",
      checklist: ["कभी भी ग्रिड बिजली बिल नहीं", "ऑटोमैटिक डस्क-टू-डॉन ऑपरेशन", "कम मेंटेनेंस, 25 वर्ष लाइफ", "सरकारी टेंडर व CSR फंडिंग के लिए पात्र"],
      tag: "स्ट्रीट लाइट सोलर",
      footerHeading: "स्ट्रीट लाइट इंस्टॉलेशन या सर्विस सपोर्ट चाहिए?",
      subsidy: { eyebrow: "सोलर स्ट्रीट लाइटिंग", big: "ज़ीरो", unit: "", footer: "बिजली बिल · सरकारी टेंडर व CSR सपोर्ट*" },
      disclaimer: "*राज्य / पंचायत / नगरपालिका बजट, CSR व GeM टेंडर से फंडेड — उपलब्धता राज्य व प्रोजेक्ट अनुसार अलग हो सकती है।",
      badge: ["सोलर", "पावर्ड"],
      benefits: [
        { iconKey: "zap", label: "ज़ीरो चलाने\nकी लागत" },
        { iconKey: "leaf", label: "स्वच्छ, हरित\nऊर्जा" },
        { iconKey: "landmark", label: "सरकारी टेंडर व\nCSR सपोर्ट" },
        { iconKey: "clock", label: "डस्क-टू-डॉन\nऑटो ऑन/ऑफ" },
        { iconKey: "shieldCheck", label: "कम\nमेंटेनेंस" },
      ],
    },
  },
  mr: {
    all: {
      tag: "रेसिडेन्शियल रूफटॉप सोलर",
      footerHeading: "नवीन इंस्टॉलेशन किंवा सर्व्हिस सपोर्ट हवा आहे?",
      subsidyFooter: "सरकारी अनुदान*",
      disclaimer: "*अनुदानाची रक्कम सिस्टिमच्या आकारावर व राज्यावर अवलंबून आहे; MNRE / PM सूर्य घर योजनेच्या नियमांनुसार.",
      badge: ["PM सूर्य", "घर योजना"],
    },
    housing_society: {
      headlineLine1: "तुमच्या संपूर्ण", headlineLine2: "सोसायटीला पॉवर द्या.",
      subheadline: "शेअर्ड सोलर. प्रत्येक रहिवाशासाठी शेअर्ड बचत.",
      paragraph: "सोसायटीच्या कॉमन लाइटिंग, लिफ्ट, पंप व EV चार्जिंग पॉइंट्ससाठी रूफटॉप सोलर बसवा — आणि सर्वांसाठी मेंटेनन्स बिल कमी करा.",
      checklist: ["कॉमन लाइटिंग, लिफ्ट व EV पॉइंट्स कव्हर", "सोसायटीसाठी 500 kW पर्यंत क्षमता", "समर्पित RWA / AOA सपोर्ट टीम", "सोसायटीच्या स्वतःच्या मीटरवर नेट मीटरिंग"],
      tag: "हाऊसिंग सोसायटी सोलर",
      footerHeading: "तुमच्या सोसायटीसाठी नवीन इंस्टॉलेशन हवे आहे?",
      subsidy: { eyebrow: "PM सूर्य घर योजना · GHS / RWA", big: "₹18,000", unit: "प्रति kW", footer: "कॉमन एरियासाठी केंद्रीय अनुदान*", showUpTo: true },
      disclaimer: "*प्रति सोसायटी 500 kW पर्यंत (डोंगराळ व NE राज्यांत ₹19,800/kW); फक्त कॉमन एरियासाठी, MNRE मार्गदर्शक तत्त्वांनुसार.",
      badge: ["GHS / RWA", "योजना"],
      benefits: [
        { iconKey: "zap", label: "कॉमन एरिया\nबिलात घट" },
        { iconKey: "leaf", label: "स्वच्छ, हरित\nऊर्जा" },
        { iconKey: "landmark", label: "₹18,000/kW\nसरकारी अनुदान" },
        { iconKey: "trendingUp", label: "उच्च रिसेल\nव्हॅल्यू" },
        { iconKey: "shieldCheck", label: "25 वर्षे\nपॅनेल लाइफ" },
      ],
    },
    street_light: {
      headlineLine1: "तुमचे रस्ते", headlineLine2: "मोफत उजळवा.",
      subheadline: "शून्य वीज बिल. शून्य ग्रिड अवलंबित्व.",
      paragraph: "नगरपालिका, पंचायती व गेटेड रोडसाठी सोलर स्ट्रीट लाइटिंग — उजळ, सुरक्षित रस्ते जे चालवण्यासाठी काहीही खर्च येत नाही.",
      checklist: ["ग्रिड वीज बिल कधीच नाही", "ऑटोमॅटिक डस्क-टू-डॉन ऑपरेशन", "कमी मेंटेनन्स, 25 वर्षे लाइफ", "सरकारी टेंडर व CSR फंडिंगसाठी पात्र"],
      tag: "स्ट्रीट लाइट सोलर",
      footerHeading: "स्ट्रीट लाइट इंस्टॉलेशन किंवा सर्व्हिस सपोर्ट हवा आहे?",
      subsidy: { eyebrow: "सोलर स्ट्रीट लाइटिंग", big: "शून्य", unit: "", footer: "वीज बिल · सरकारी टेंडर व CSR सपोर्ट*" },
      disclaimer: "*राज्य / पंचायत / नगरपालिका बजेट, CSR व GeM टेंडरद्वारे अनुदानित — उपलब्धता राज्य व प्रकल्पानुसार बदलते.",
      badge: ["सोलर", "पॉवर्ड"],
      benefits: [
        { iconKey: "zap", label: "शून्य चालवण्याचा\nखर्च" },
        { iconKey: "leaf", label: "स्वच्छ, हरित\nऊर्जा" },
        { iconKey: "landmark", label: "सरकारी टेंडर व\nCSR सपोर्ट" },
        { iconKey: "clock", label: "डस्क-टू-डॉन\nऑटो ऑन/ऑफ" },
        { iconKey: "shieldCheck", label: "कमी\nमेंटेनन्स" },
      ],
    },
  },
  gu: {
    all: {
      tag: "રેસિડેન્શિયલ રૂફટોપ સોલર",
      footerHeading: "નવું ઇન્સ્ટોલેશન કે સર્વિસ સપોર્ટ જોઈએ છે?",
      subsidyFooter: "સરકારી સબસિડી*",
      disclaimer: "*સબસિડીની રકમ સિસ્ટમના કદ અને રાજ્ય પર આધારિત છે; MNRE / PM સૂર્ય ઘર યોજનાના નિયમો અનુસાર.",
      badge: ["PM સૂર્ય", "ઘર યોજના"],
    },
    housing_society: {
      headlineLine1: "તમારી આખી", headlineLine2: "સોસાયટીને પાવર આપો.",
      subheadline: "શેર કરેલું સોલર. દરેક રહેવાસી માટે શેર કરેલી બચત.",
      paragraph: "તમારી સોસાયટીની કોમન લાઇટિંગ, લિફ્ટ, પંપ અને EV ચાર્જિંગ પોઇન્ટ્સ માટે રૂફટોપ સોલર લગાવો — અને બધા માટે મેઇન્ટેનન્સ બિલ ઘટાડો.",
      checklist: ["કોમન લાઇટિંગ, લિફ્ટ અને EV પોઇન્ટ્સ કવર", "સોસાયટી દીઠ 500 kW સુધીની ક્ષમતા", "સમર્પિત RWA / AOA સપોર્ટ ટીમ", "સોસાયટીના પોતાના મીટર પર નેટ મીટરિંગ"],
      tag: "હાઉસિંગ સોસાયટી સોલર",
      footerHeading: "તમારી સોસાયટી માટે નવું ઇન્સ્ટોલેશન જોઈએ છે?",
      subsidy: { eyebrow: "PM સૂર્ય ઘર યોજના · GHS / RWA", big: "₹18,000", unit: "પ્રતિ kW", footer: "કોમન એરિયા માટે કેન્દ્રીય સબસિડી*", showUpTo: true },
      disclaimer: "*સોસાયટી દીઠ 500 kW સુધી (પહાડી અને NE રાજ્યોમાં ₹19,800/kW); ફક્ત કોમન એરિયા માટે, MNRE માર્ગદર્શિકા મુજબ.",
      badge: ["GHS / RWA", "યોજના"],
      benefits: [
        { iconKey: "zap", label: "કોમન એરિયા\nબિલમાં ઘટાડો" },
        { iconKey: "leaf", label: "સ્વચ્છ, હરિત\nઊર્જા" },
        { iconKey: "landmark", label: "₹18,000/kW\nસરકારી સબસિડી" },
        { iconKey: "trendingUp", label: "ઊંચું રિસેલ\nમૂલ્ય" },
        { iconKey: "shieldCheck", label: "25 વર્ષ\nપેનલ લાઇફ" },
      ],
    },
    street_light: {
      headlineLine1: "તમારા રસ્તાઓને", headlineLine2: "મફતમાં પ્રકાશિત કરો.",
      subheadline: "શૂન્ય વીજ બિલ. શૂન્ય ગ્રિડ નિર્ભરતા.",
      paragraph: "નગરપાલિકાઓ, પંચાયતો અને ગેટેડ રોડ માટે સોલર સ્ટ્રીટ લાઇટિંગ — તેજસ્વી, સુરક્ષિત રસ્તા જે ચલાવવા માટે કંઈ ખર્ચ નથી.",
      checklist: ["ક્યારેય ગ્રિડ વીજ બિલ નહીં", "ઓટોમેટિક ડસ્ક-ટુ-ડોન ઓપરેશન", "ઓછું મેઇન્ટેનન્સ, 25 વર્ષ લાઇફ", "સરકારી ટેન્ડર અને CSR ફંડિંગ માટે પાત્ર"],
      tag: "સ્ટ્રીટ લાઇટ સોલર",
      footerHeading: "સ્ટ્રીટ લાઇટ ઇન્સ્ટોલેશન કે સર્વિસ સપોર્ટ જોઈએ છે?",
      subsidy: { eyebrow: "સોલર સ્ટ્રીટ લાઇટિંગ", big: "શૂન્ય", unit: "", footer: "વીજ બિલ · સરકારી ટેન્ડર અને CSR સપોર્ટ*" },
      disclaimer: "*રાજ્ય / પંચાયત / નગરપાલિકા બજેટ, CSR અને GeM ટેન્ડર દ્વારા ફંડેડ — ઉપલબ્ધતા રાજ્ય અને પ્રોજેક્ટ પ્રમાણે બદલાય છે.",
      badge: ["સોલર", "પાવર્ડ"],
      benefits: [
        { iconKey: "zap", label: "શૂન્ય ચલાવવાનો\nખર્ચ" },
        { iconKey: "leaf", label: "સ્વચ્છ, હરિત\nઊર્જા" },
        { iconKey: "landmark", label: "સરકારી ટેન્ડર અને\nCSR સપોર્ટ" },
        { iconKey: "clock", label: "ડસ્ક-ટુ-ડોન\nઓટો ઓન/ઓફ" },
        { iconKey: "shieldCheck", label: "ઓછું\nમેઇન્ટેનન્સ" },
      ],
    },
  },
  ta: {
    all: {
      tag: "குடியிருப்பு கூரை சோலார்",
      footerHeading: "புதிய நிறுவல் அல்லது சர்வீஸ் ஆதரவு தேவையா?",
      subsidyFooter: "அரசு மானியம்*",
      disclaimer: "*மானிய தொகை கணினி அளவு & மாநிலத்தைப் பொறுத்தது; MNRE / PM சூர்ய கர் யோஜனா வழிகாட்டுதல்களுக்கு உட்பட்டது.",
      badge: ["PM சூர்ய", "கர் யோஜனா"],
    },
    housing_society: {
      headlineLine1: "உங்கள் முழு", headlineLine2: "சொசைட்டிக்கும் பவர் கொடுங்கள்.",
      subheadline: "பகிரப்பட்ட சோலார். ஒவ்வொரு குடியிருப்பாளருக்கும் பகிரப்பட்ட சேமிப்பு.",
      paragraph: "உங்கள் சொசைட்டியின் பொதுவான லைட்டிங், லிஃப்ட், பம்ப் & EV சார்ஜிங் பாயிண்ட்களுக்கு ரூஃப்டாப் சோலார் பொருத்துங்கள் — அனைவருக்கும் மெயின்டெனன்ஸ் பில்லைக் குறையுங்கள்.",
      checklist: ["பொதுவான லைட்டிங், லிஃப்ட் & EV பாயிண்ட்களை உள்ளடக்கியது", "ஒரு சொசைட்டிக்கு 500 kW வரை திறன்", "பிரத்யேக RWA / AOA ஆதரவு குழு", "சொசைட்டியின் சொந்த மீட்டரில் நெட் மீட்டரிங்"],
      tag: "ஹவுசிங் சொசைட்டி சோலார்",
      footerHeading: "உங்கள் சொசைட்டிக்கு புதிய நிறுவல் தேவையா?",
      subsidy: { eyebrow: "PM சூர்ய கர் யோஜனா · GHS / RWA", big: "₹18,000", unit: "ஒரு kW க்கு", footer: "பொதுவான பகுதிகளுக்கான மத்திய மானியம்*", showUpTo: true },
      disclaimer: "*ஒரு சொசைட்டிக்கு 500 kW வரை (மலை & NE மாநிலங்களில் ₹19,800/kW); பொதுவான பகுதிகளுக்கு மட்டும், MNRE வழிகாட்டுதல்களின்படி.",
      badge: ["GHS / RWA", "திட்டம்"],
      benefits: [
        { iconKey: "zap", label: "பொதுவான பகுதி\nபில் குறைவு" },
        { iconKey: "leaf", label: "சுத்தமான, பசுமை\nஆற்றல்" },
        { iconKey: "landmark", label: "₹18,000/kW\nஅரசு மானியம்" },
        { iconKey: "trendingUp", label: "அதிக மறுவிற்பனை\nமதிப்பு" },
        { iconKey: "shieldCheck", label: "25 ஆண்டு\nபேனல் ஆயுள்" },
      ],
    },
    street_light: {
      headlineLine1: "உங்கள் தெருக்களை", headlineLine2: "இலவசமாக ஒளிரச் செய்யுங்கள்.",
      subheadline: "பூஜ்ஜிய மின்சார பில். பூஜ்ஜிய கிரிட் சார்பு.",
      paragraph: "நகராட்சிகள், பஞ்சாயத்துகள் & கேட்டட் சாலைகளுக்கான சோலார் தெரு விளக்குகள் — இயக்க எந்த செலவும் இல்லாத பிரகாசமான, பாதுகாப்பான தெருக்கள்.",
      checklist: ["எப்போதும் கிரிட் மின்சார பில் இல்லை", "தானியங்கி டஸ்க்-டு-டான் இயக்கம்", "குறைந்த பராமரிப்பு, 25 ஆண்டு ஆயுள்", "அரசு டெண்டர் & CSR நிதிக்கு தகுதியானது"],
      tag: "தெரு விளக்கு சோலார்",
      footerHeading: "தெரு விளக்கு நிறுவல் அல்லது சர்வீஸ் ஆதரவு தேவையா?",
      subsidy: { eyebrow: "சோலார் தெரு விளக்கு", big: "பூஜ்ஜியம்", unit: "", footer: "மின்சார பில் · அரசு டெண்டர் & CSR ஆதரவு*" },
      disclaimer: "*மாநில / பஞ்சாயத்து / நகராட்சி பட்ஜெட், CSR & GeM டெண்டர் மூலம் நிதியளிக்கப்படுகிறது — கிடைக்கும் தன்மை மாநிலம் & திட்டத்தைப் பொறுத்து மாறுபடும்.",
      badge: ["சோலார்", "இயக்கப்படுகிறது"],
      benefits: [
        { iconKey: "zap", label: "பூஜ்ஜிய இயக்க\nசெலவு" },
        { iconKey: "leaf", label: "சுத்தமான, பசுமை\nஆற்றல்" },
        { iconKey: "landmark", label: "அரசு டெண்டர் &\nCSR ஆதரவு" },
        { iconKey: "clock", label: "டஸ்க்-டு-டான்\nஆட்டோ ஆன்/ஆஃப்" },
        { iconKey: "shieldCheck", label: "குறைந்த\nபராமரிப்பு" },
      ],
    },
  },
  te: {
    all: {
      tag: "రెసిడెన్షియల్ రూఫ్‌టాప్ సోలార్",
      footerHeading: "కొత్త ఇన్‌స్టాలేషన్ లేదా సర్వీస్ సపోర్ట్ కావాలా?",
      subsidyFooter: "ప్రభుత్వ సబ్సిడీ*",
      disclaimer: "*సబ్సిడీ మొత్తం సిస్టమ్ పరిమాణం & రాష్ట్రంపై ఆధారపడి ఉంటుంది; MNRE / PM సూర్య ఘర్ యోజన మార్గదర్శకాల ప్రకారం.",
      badge: ["PM సూర్య", "ఘర్ యోజన"],
    },
    housing_society: {
      headlineLine1: "మీ మొత్తం", headlineLine2: "సొసైటీకి పవర్ ఇవ్వండి.",
      subheadline: "షేర్డ్ సోలార్. ప్రతి నివాసికి షేర్డ్ సేవింగ్స్.",
      paragraph: "మీ సొసైటీ కామన్ లైటింగ్, లిఫ్ట్‌లు, పంపులు & EV ఛార్జింగ్ పాయింట్ల కోసం రూఫ్‌టాప్ సోలార్ పెట్టండి — అందరికీ మెయింటెనెన్స్ బిల్లు తగ్గించండి.",
      checklist: ["కామన్ లైటింగ్, లిఫ్ట్‌లు & EV పాయింట్లను కవర్ చేస్తుంది", "సొసైటీకి 500 kW వరకు సామర్థ్యం", "ప్రత్యేక RWA / AOA సపోర్ట్ టీమ్", "సొసైటీ సొంత మీటర్‌పై నెట్ మీటరింగ్"],
      tag: "హౌసింగ్ సొసైటీ సోలార్",
      footerHeading: "మీ సొసైటీకి కొత్త ఇన్‌స్టాలేషన్ కావాలా?",
      subsidy: { eyebrow: "PM సూర్య ఘర్ యోజన · GHS / RWA", big: "₹18,000", unit: "ప్రతి kW కి", footer: "కామన్ ఏరియాల కోసం కేంద్ర సబ్సిడీ*", showUpTo: true },
      disclaimer: "*సొసైటీకి 500 kW వరకు (కొండ & NE రాష్ట్రాల్లో ₹19,800/kW); కామన్ ఏరియాలకు మాత్రమే, MNRE మార్గదర్శకాల ప్రకారం.",
      badge: ["GHS / RWA", "స్కీమ్"],
      benefits: [
        { iconKey: "zap", label: "కామన్ ఏరియా\nబిల్లులో తగ్గింపు" },
        { iconKey: "leaf", label: "శుభ్రమైన, పునరుత్పాదక\nశక్తి" },
        { iconKey: "landmark", label: "₹18,000/kW\nప్రభుత్వ సబ్సిడీ" },
        { iconKey: "trendingUp", label: "అధిక రీసేల్\nవిలువ" },
        { iconKey: "shieldCheck", label: "25 సంవత్సరాల\nపానెల్ లైఫ్" },
      ],
    },
    street_light: {
      headlineLine1: "మీ వీధులను", headlineLine2: "ఉచితంగా వెలిగించండి.",
      subheadline: "జీరో విద్యుత్ బిల్లు. జీరో గ్రిడ్ ఆధారపడటం.",
      paragraph: "మునిసిపాలిటీలు, పంచాయతీలు & గేటెడ్ రోడ్ల కోసం సోలార్ స్ట్రీట్ లైటింగ్ — నడపడానికి ఖర్చు లేని ప్రకాశవంతమైన, సురక్షితమైన వీధులు.",
      checklist: ["గ్రిడ్ విద్యుత్ బిల్లు ఎప్పుడూ ఉండదు", "ఆటోమేటిక్ డస్క్-టు-డాన్ ఆపరేషన్", "తక్కువ నిర్వహణ, 25 సంవత్సరాల జీవితకాలం", "ప్రభుత్వ టెండర్లు & CSR ఫండింగ్‌కు అర్హత"],
      tag: "స్ట్రీట్ లైట్ సోలార్",
      footerHeading: "స్ట్రీట్ లైట్ ఇన్‌స్టాలేషన్ లేదా సర్వీస్ సపోర్ట్ కావాలా?",
      subsidy: { eyebrow: "సోలార్ స్ట్రీట్ లైటింగ్", big: "జీరో", unit: "", footer: "విద్యుత్ బిల్లు · ప్రభుత్వ టెండర్ & CSR సపోర్ట్*" },
      disclaimer: "*రాష్ట్ర / పంచాయతీ / మునిసిపల్ బడ్జెట్లు, CSR & GeM టెండర్ల ద్వారా నిధులు — లభ్యత రాష్ట్రం & ప్రాజెక్టు ప్రకారం మారుతుంది.",
      badge: ["సోలార్", "పవర్డ్"],
      benefits: [
        { iconKey: "zap", label: "జీరో నిర్వహణ\nఖర్చు" },
        { iconKey: "leaf", label: "శుభ్రమైన, పునరుత్పాదక\nశక్తి" },
        { iconKey: "landmark", label: "ప్రభుత్వ టెండర్ &\nCSR సపోర్ట్" },
        { iconKey: "clock", label: "డస్క్-టు-డాన్\nఆటో ఆన్/ఆఫ్" },
        { iconKey: "shieldCheck", label: "తక్కువ\nనిర్వహణ" },
      ],
    },
  },
  kn: {
    all: {
      tag: "ರೆಸಿಡೆನ್ಷಿಯಲ್ ರೂಫ್‌ಟಾಪ್ ಸೌರಶಕ್ತಿ",
      footerHeading: "ಹೊಸ ಅನುಸ್ಥಾಪನೆ ಅಥವಾ ಸೇವಾ ಬೆಂಬಲ ಬೇಕೇ?",
      subsidyFooter: "ಸರ್ಕಾರಿ ಸಬ್ಸಿಡಿ*",
      disclaimer: "*ಸಬ್ಸಿಡಿ ಮೊತ್ತವು ಸಿಸ್ಟಮ್ ಗಾತ್ರ & ರಾಜ್ಯವನ್ನು ಅವಲಂಬಿಸಿದೆ; MNRE / PM ಸೂರ್ಯ ಘರ್ ಯೋಜನೆಯ ಮಾರ್ಗಸೂಚಿಗಳಿಗೆ ಒಳಪಟ್ಟಿದೆ.",
      badge: ["PM ಸೂರ್ಯ", "ಘರ್ ಯೋಜನೆ"],
    },
    housing_society: {
      headlineLine1: "ನಿಮ್ಮ ಇಡೀ", headlineLine2: "ಸೊಸೈಟಿಗೆ ಪವರ್ ನೀಡಿ.",
      subheadline: "ಹಂಚಿಕೊಂಡ ಸೌರಶಕ್ತಿ. ಪ್ರತಿ ನಿವಾಸಿಗೆ ಹಂಚಿಕೊಂಡ ಉಳಿತಾಯ.",
      paragraph: "ನಿಮ್ಮ ಸೊಸೈಟಿಯ ಕಾಮನ್ ಲೈಟಿಂಗ್, ಲಿಫ್ಟ್‌ಗಳು, ಪಂಪ್‌ಗಳು & EV ಚಾರ್ಜಿಂಗ್ ಪಾಯಿಂಟ್‌ಗಳಿಗಾಗಿ ರೂಫ್‌ಟಾಪ್ ಸೌರಶಕ್ತಿ ಅಳವಡಿಸಿ — ಮತ್ತು ಎಲ್ಲರ ನಿರ್ವಹಣಾ ಬಿಲ್ ಕಡಿಮೆ ಮಾಡಿ.",
      checklist: ["ಕಾಮನ್ ಲೈಟಿಂಗ್, ಲಿಫ್ಟ್ & EV ಪಾಯಿಂಟ್‌ಗಳನ್ನು ಒಳಗೊಂಡಿದೆ", "ಸೊಸೈಟಿಗೆ 500 kW ವರೆಗೆ ಸಾಮರ್ಥ್ಯ", "ಮೀಸಲಾದ RWA / AOA ಬೆಂಬಲ ತಂಡ", "ಸೊಸೈಟಿಯ ಸ್ವಂತ ಮೀಟರ್‌ನಲ್ಲಿ ನೆಟ್ ಮೀಟರಿಂಗ್"],
      tag: "ಹೌಸಿಂಗ್ ಸೊಸೈಟಿ ಸೌರಶಕ್ತಿ",
      footerHeading: "ನಿಮ್ಮ ಸೊಸೈಟಿಗೆ ಹೊಸ ಅನುಸ್ಥಾಪನೆ ಬೇಕೇ?",
      subsidy: { eyebrow: "PM ಸೂರ್ಯ ಘರ್ ಯೋಜನೆ · GHS / RWA", big: "₹18,000", unit: "ಪ್ರತಿ kW ಗೆ", footer: "ಕಾಮನ್ ಏರಿಯಾಗಳಿಗೆ ಕೇಂದ್ರ ಸಬ್ಸಿಡಿ*", showUpTo: true },
      disclaimer: "*ಪ್ರತಿ ಸೊಸೈಟಿಗೆ 500 kW ವರೆಗೆ (ಗುಡ್ಡಗಾಡು & NE ರಾಜ್ಯಗಳಲ್ಲಿ ₹19,800/kW); ಕಾಮನ್ ಏರಿಯಾಗಳಿಗೆ ಮಾತ್ರ, MNRE ಮಾರ್ಗಸೂಚಿಗಳ ಪ್ರಕಾರ.",
      badge: ["GHS / RWA", "ಯೋಜನೆ"],
      benefits: [
        { iconKey: "zap", label: "ಕಾಮನ್ ಏರಿಯಾ\nಬಿಲ್‌ನಲ್ಲಿ ಕಡಿತ" },
        { iconKey: "leaf", label: "ಶುದ್ಧ, ಹಸಿರು\nಶಕ್ತಿ" },
        { iconKey: "landmark", label: "₹18,000/kW\nಸರ್ಕಾರಿ ಸಬ್ಸಿಡಿ" },
        { iconKey: "trendingUp", label: "ಹೆಚ್ಚಿನ ಮರುಮಾರಾಟ\nಮೌಲ್ಯ" },
        { iconKey: "shieldCheck", label: "25 ವರ್ಷ\nಪ್ಯಾನಲ್ ಬಾಳಿಕೆ" },
      ],
    },
    street_light: {
      headlineLine1: "ನಿಮ್ಮ ರಸ್ತೆಗಳನ್ನು", headlineLine2: "ಉಚಿತವಾಗಿ ಬೆಳಗಿಸಿ.",
      subheadline: "ಶೂನ್ಯ ವಿದ್ಯುತ್ ಬಿಲ್. ಶೂನ್ಯ ಗ್ರಿಡ್ ಅವಲಂಬನೆ.",
      paragraph: "ಮುನ್ಸಿಪಾಲಿಟಿಗಳು, ಪಂಚಾಯತ್‌ಗಳು & ಗೇಟೆಡ್ ರಸ್ತೆಗಳಿಗಾಗಿ ಸೌರ ಬೀದಿ ದೀಪ — ನಡೆಸಲು ಯಾವುದೇ ವೆಚ್ಚವಿಲ್ಲದ ಪ್ರಕಾಶಮಾನ, ಸುರಕ್ಷಿತ ರಸ್ತೆಗಳು.",
      checklist: ["ಗ್ರಿಡ್ ವಿದ್ಯುತ್ ಬಿಲ್ ಎಂದಿಗೂ ಇಲ್ಲ", "ಸ್ವಯಂಚಾಲಿತ ಡಸ್ಕ್-ಟು-ಡಾನ್ ಕಾರ್ಯಾಚರಣೆ", "ಕಡಿಮೆ ನಿರ್ವಹಣೆ, 25 ವರ್ಷ ಬಾಳಿಕೆ", "ಸರ್ಕಾರಿ ಟೆಂಡರ್ & CSR ಫಂಡಿಂಗ್‌ಗೆ ಅರ್ಹ"],
      tag: "ಬೀದಿ ದೀಪ ಸೌರಶಕ್ತಿ",
      footerHeading: "ಬೀದಿ ದೀಪ ಅನುಸ್ಥಾಪನೆ ಅಥವಾ ಸೇವಾ ಬೆಂಬಲ ಬೇಕೇ?",
      subsidy: { eyebrow: "ಸೌರ ಬೀದಿ ದೀಪ", big: "ಶೂನ್ಯ", unit: "", footer: "ವಿದ್ಯುತ್ ಬಿಲ್ · ಸರ್ಕಾರಿ ಟೆಂಡರ್ & CSR ಬೆಂಬಲ*" },
      disclaimer: "*ರಾಜ್ಯ / ಪಂಚಾಯತ್ / ಮುನ್ಸಿಪಲ್ ಬಜೆಟ್, CSR & GeM ಟೆಂಡರ್‌ಗಳ ಮೂಲಕ ಹಣಕಾಸು — ಲಭ್ಯತೆ ರಾಜ್ಯ & ಯೋಜನೆಯ ಪ್ರಕಾರ ಬದಲಾಗುತ್ತದೆ.",
      badge: ["ಸೌರಶಕ್ತಿ", "ಚಾಲಿತ"],
      benefits: [
        { iconKey: "zap", label: "ಶೂನ್ಯ ನಿರ್ವಹಣಾ\nವೆಚ್ಚ" },
        { iconKey: "leaf", label: "ಶುದ್ಧ, ಹಸಿರು\nಶಕ್ತಿ" },
        { iconKey: "landmark", label: "ಸರ್ಕಾರಿ ಟೆಂಡರ್ &\nCSR ಬೆಂಬಲ" },
        { iconKey: "clock", label: "ಡಸ್ಕ್-ಟು-ಡಾನ್\nಆಟೋ ಆನ್/ಆಫ್" },
        { iconKey: "shieldCheck", label: "ಕಡಿಮೆ\nನಿರ್ವಹಣೆ" },
      ],
    },
  },
  bn: {
    all: {
      tag: "রেসিডেনশিয়াল রুফটপ সোলার",
      footerHeading: "নতুন ইনস্টলেশন বা সার্ভিস সাপোর্ট দরকার?",
      subsidyFooter: "সরকারি ভর্তুকি*",
      disclaimer: "*ভর্তুকির পরিমাণ সিস্টেমের আকার ও রাজ্যের উপর নির্ভরশীল; MNRE / PM সূর্য ঘর যোজনার নির্দেশিকা অনুযায়ী।",
      badge: ["PM সূর্য", "ঘর যোজনা"],
    },
    housing_society: {
      headlineLine1: "আপনার পুরো", headlineLine2: "সোসাইটিকে পাওয়ার দিন।",
      subheadline: "শেয়ার করা সোলার। প্রতিটি বাসিন্দার জন্য শেয়ার করা সাশ্রয়।",
      paragraph: "আপনার সোসাইটির কমন লাইটিং, লিফট, পাম্প ও EV চার্জিং পয়েন্টের জন্য রুফটপ সোলার বসান — এবং সবার জন্য রক্ষণাবেক্ষণ বিল কমান।",
      checklist: ["কমন লাইটিং, লিফট ও EV পয়েন্ট কভার করে", "প্রতি সোসাইটিতে 500 kW পর্যন্ত ক্ষমতা", "নিবেদিত RWA / AOA সাপোর্ট টিম", "সোসাইটির নিজস্ব মিটারে নেট মিটারিং"],
      tag: "হাউজিং সোসাইটি সোলার",
      footerHeading: "আপনার সোসাইটির জন্য নতুন ইনস্টলেশন দরকার?",
      subsidy: { eyebrow: "PM সূর্য ঘর যোজনা · GHS / RWA", big: "₹18,000", unit: "প্রতি kW", footer: "কমন এরিয়ার জন্য কেন্দ্রীয় ভর্তুকি*", showUpTo: true },
      disclaimer: "*প্রতি সোসাইটিতে 500 kW পর্যন্ত (পাহাড়ি ও NE রাজ্যে ₹19,800/kW); শুধুমাত্র কমন এরিয়ার জন্য, MNRE নির্দেশিকা অনুযায়ী।",
      badge: ["GHS / RWA", "যোজনা"],
      benefits: [
        { iconKey: "zap", label: "কমন এরিয়া\nবিলে সাশ্রয়" },
        { iconKey: "leaf", label: "পরিষ্কার, সবুজ\nশক্তি" },
        { iconKey: "landmark", label: "₹18,000/kW\nসরকারি ভর্তুকি" },
        { iconKey: "trendingUp", label: "উচ্চ পুনঃবিক্রয়\nমূল্য" },
        { iconKey: "shieldCheck", label: "25 বছরের\nপ্যানেল লাইফ" },
      ],
    },
    street_light: {
      headlineLine1: "আপনার রাস্তা", headlineLine2: "বিনামূল্যে আলোকিত করুন।",
      subheadline: "শূন্য বিদ্যুৎ বিল। শূন্য গ্রিড নির্ভরতা।",
      paragraph: "পৌরসভা, পঞ্চায়েত ও গেটেড রোডের জন্য সোলার স্ট্রিট লাইটিং — উজ্জ্বল, নিরাপদ রাস্তা যা চালাতে কোনো খরচ হয় না।",
      checklist: ["কখনোই গ্রিড বিদ্যুৎ বিল নয়", "স্বয়ংক্রিয় ডাস্ক-টু-ডন অপারেশন", "কম রক্ষণাবেক্ষণ, 25 বছরের আয়ু", "সরকারি টেন্ডার ও CSR ফান্ডিংয়ের জন্য যোগ্য"],
      tag: "স্ট্রিট লাইট সোলার",
      footerHeading: "স্ট্রিট লাইট ইনস্টলেশন বা সার্ভিস সাপোর্ট দরকার?",
      subsidy: { eyebrow: "সোলার স্ট্রিট লাইটিং", big: "শূন্য", unit: "", footer: "বিদ্যুৎ বিল · সরকারি টেন্ডার ও CSR সাপোর্ট*" },
      disclaimer: "*রাজ্য / পঞ্চায়েত / পৌরসভার বাজেট, CSR ও GeM টেন্ডারের মাধ্যমে অর্থায়িত — প্রাপ্যতা রাজ্য ও প্রকল্প অনুযায়ী পরিবর্তিত হয়।",
      badge: ["সোলার", "চালিত"],
      benefits: [
        { iconKey: "zap", label: "শূন্য চালানোর\nখরচ" },
        { iconKey: "leaf", label: "পরিষ্কার, সবুজ\nশক্তি" },
        { iconKey: "landmark", label: "সরকারি টেন্ডার ও\nCSR সাপোর্ট" },
        { iconKey: "clock", label: "ডাস্ক-টু-ডন\nঅটো অন/অফ" },
        { iconKey: "shieldCheck", label: "কম\nরক্ষণাবেক্ষণ" },
      ],
    },
  },
};
